import { isHeic } from "./media/format";

/** アイコンの一辺。プロフィール表示にしか使わないので小さくてよい */
const AVATAR_EDGE = 256;
const AVATAR_QUALITY = 0.85;

/** DB の列に直接入れるため上限を絞る(縮小後は数十KBに収まる想定) */
const AVATAR_MAX_LENGTH = 300_000;

/**
 * 選ばれた画像を、中央の正方形で切り出した 256px の WebP data URL にする。
 * R2 を経由せずプロフィール行と一緒に読めるよう、文字列で返す。
 */
export async function prepareAvatar(file: File): Promise<string> {
  let blob: Blob = file;
  if (isHeic(file)) {
    // HEICデコーダは数MBある。選んだときだけ読み込む(→ lib/media/prepare.ts と同じ)
    const { heicTo } = await import("heic-to");
    const png: unknown = await heicTo({ blob: file, type: "image/png" });
    if (!(png instanceof Blob)) {
      throw new Error("HEIC の変換に失敗しました");
    }
    blob = png;
  }

  const bitmap = await createImageBitmap(blob);
  const edge = Math.min(bitmap.width, bitmap.height);
  const sourceX = (bitmap.width - edge) / 2;
  const sourceY = (bitmap.height - edge) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_EDGE;
  canvas.height = AVATAR_EDGE;
  const context = canvas.getContext("2d");
  if (context === null) throw new Error("Canvas を初期化できません");
  context.drawImage(bitmap, sourceX, sourceY, edge, edge, 0, 0, AVATAR_EDGE, AVATAR_EDGE);
  bitmap.close();

  const webp = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result === null) reject(new Error("画像の書き出しに失敗しました"));
        else resolve(result);
      },
      "image/webp",
      AVATAR_QUALITY,
    );
  });

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error("画像の読み込みに失敗しました"));
    };
    reader.readAsDataURL(webp);
  });

  if (dataUrl.length > AVATAR_MAX_LENGTH) {
    throw new Error("画像を十分に小さくできませんでした");
  }
  return dataUrl;
}
